'use client';

import { liveUpdateAttrs } from 'libs/contentful/utils';
import RichTextCopy from 'components/shared/RichTextCopy/RichTextCopy';
import RichTextHeadline from 'components/shared/RichTextHeadline/RichTextHeadline';

import Eyebrow from 'components/shared/Eyebrow/Eyebrow';
import CTA, { isValidCta } from 'components/shared/CTA/CTA';

import {
  CONTENT_FEATURE_CAROUSEL_VARIANTS,
  ContentFeatureCarouselClassNames,
  ContentFeatureCarouselProps,
  ContentFeatureCarouselVariant,
} from './types';

import { cn, getColorway, getYMargins } from 'utils/styles';
import CardCarousel from '../CardCarousel/CardCarousel';
import { Margins } from '@/types/constants';
import { CarouselPaginationThresholdProp } from '../CardCarousel/types';
import { useRef } from 'react';

const paginationThreshold: CarouselPaginationThresholdProp = (isAboveLg, isAboveMd) =>
  isAboveLg ? 2 : isAboveMd ? 2 : 1;

const ContentFeatureCarousel = ({
  sys,
  sectionName,
  color,
  margins,
  variant = CONTENT_FEATURE_CAROUSEL_VARIANTS.Base,
  eyebrow,
  headline,
  copy,
  cta,
  cardCarousel,
  className,
}: ContentFeatureCarouselProps) => {
  const paginationRef = useRef<HTMLDivElement>(null);

  const baseClassNames: ContentFeatureCarouselClassNames = {
    section: cn(className?.section),
    wrapper: cn(
      'content-wrapper overflow-hidden flex flex-col sm:grid sm:grid-cols-[20rem_auto] gap-10 sm:gap-34',
      className?.wrapper,
    ),
    header: {
      wrapper: cn('flex flex-col gap-6', className?.header?.wrapper),
      eyebrow: cn('mb-0', className?.header?.eyebrow),
      headline: cn('text-heading-lg-fixed font-sans', className?.header?.headline),
    },
    copy: cn('flex-1 font-serif text-lg', className?.copy),
    cta: cn(className?.cta),
    cardCarousel: {
      ...className?.cardCarousel,
      wrapper: cn(className?.cardCarousel?.wrapper),
      carousel: cn(className?.cardCarousel?.carousel),
      pagination: cn(className?.cardCarousel?.pagination),
      cardGrid: cn(className?.cardCarousel?.cardGrid),
      iconVariant: {
        ...className?.cardCarousel?.iconVariant,
        cardWrapper: {
          ...className?.cardCarousel?.iconVariant?.cardWrapper,
          wrapper: cn(className?.cardCarousel?.iconVariant?.cardWrapper?.wrapper),
          header: cn(className?.cardCarousel?.iconVariant?.cardWrapper?.header),
        },
      },
      imageVariant: {
        ...className?.cardCarousel?.imageVariant,
        cardWrapper: {
          ...className?.cardCarousel?.imageVariant?.cardWrapper,
          wrapper: cn(className?.cardCarousel?.imageVariant?.cardWrapper?.wrapper),
          header: cn(className?.cardCarousel?.imageVariant?.cardWrapper?.header),
        },
      },
      textVariant: {
        ...className?.cardCarousel?.textVariant,
        cardWrapper: {
          ...className?.cardCarousel?.textVariant?.cardWrapper,
          wrapper: cn(className?.cardCarousel?.textVariant?.cardWrapper?.wrapper),
          header: cn(className?.cardCarousel?.textVariant?.cardWrapper?.header),
        },
      },
      videoVariant: {
        ...className?.cardCarousel?.videoVariant,
        transitions: {
          playing: cn(className?.cardCarousel?.videoVariant?.transitions?.playing),
          paused: cn(className?.cardCarousel?.videoVariant?.transitions?.paused),
        },
        cardWrapper: {
          ...className?.cardCarousel?.videoVariant?.cardWrapper,
          wrapper: cn(className?.cardCarousel?.videoVariant?.cardWrapper?.wrapper),
          header: cn(className?.cardCarousel?.videoVariant?.cardWrapper?.header),
        },
      },
    },
  };

  // Type for card variant overrides used by the factory
  interface VariantOverrides {
    cardWrapper?: { wrapper?: string; header?: string };
    cardContent?: { wrapper?: string; header?: { wrapper?: string; eyebrow?: string; headline?: string } };
    transitions?: { playing?: string; paused?: string };
    media?: { wrapper?: string; content?: string };
  }

  // Factory for ITG card variant styles - reduces repetition across icon/image/text/video variants
  const createItgCardVariant = (variantOverrides?: VariantOverrides, options?: { withMedia?: boolean }) => {
    const cardWrapper = 'min-h-[555px] sm:min-h-[755px] min-w-[312px] sm:w-[312px] py-8 sm:py-8 overflow-hidden';
    const contentWrapper = 'justify-end mx-6 sm:mx-7 z-10';
    const headline = ['text-heading-sm', 'sm:text-heading-md'];

    const base = {
      cardWrapper: {
        wrapper: cn(cardWrapper, variantOverrides?.cardWrapper?.wrapper),
        header: cn('hidden', variantOverrides?.cardWrapper?.header),
      },
      cardContent: {
        wrapper: cn(contentWrapper, variantOverrides?.cardContent?.wrapper),
        header: {
          wrapper: cn(variantOverrides?.cardContent?.header?.wrapper),
          eyebrow: cn(variantOverrides?.cardContent?.header?.eyebrow),
          headline: cn(...headline, variantOverrides?.cardContent?.header?.headline),
        },
      },
    };

    if (!options?.withMedia) return base;

    return {
      ...base,
      media: {
        wrapper: cn('p-0 sm:p-0 absolute top-0 left-0 w-full h-full', variantOverrides?.media?.wrapper),
        content: cn('object-cover', variantOverrides?.media?.content),
      },
    };
  };

  const itgClassNames: ContentFeatureCarouselClassNames = {
    section: cn('overflow-hidden', className?.section),
    wrapper: cn('content-wrapper flex flex-col sm:grid sm:grid-cols-[20rem_auto] gap-10 sm:gap-34', className?.wrapper),
    header: {
      wrapper: cn('flex flex-col gap-4', className?.header?.wrapper),
      eyebrow: cn('mb-0', className?.header?.eyebrow),
      headline: cn('text-heading-md sm:text-heading-lg-fixed font-sans', className?.header?.headline),
    },
    copy: cn('flex-1 font-serif text-lg', className?.copy),
    cta: cn('-mt-2 sm:w-fit sm:mb-18', className?.cta),
    cardCarousel: {
      ...className?.cardCarousel,
      wrapper: cn(className?.cardCarousel?.wrapper),
      carousel: cn('overflow-visible sm:overflow-hidden sm:rounded-l-lg', className?.cardCarousel?.carousel),
      pagination: cn('mt-8 sm:mt-4', className?.cardCarousel?.pagination),
      cardGrid: cn('gap-2 sm:gap-6', className?.cardCarousel?.cardGrid),
      iconVariant: {
        icon: cn('mx-2 sm:mx-4', className?.cardCarousel?.iconVariant?.icon),
        cta: cn('mx-6 sm:mx-6', className?.cardCarousel?.iconVariant?.cta),
        ...createItgCardVariant(className?.cardCarousel?.iconVariant),
      },
      imageVariant: createItgCardVariant(className?.cardCarousel?.imageVariant, { withMedia: true }),
      textVariant: createItgCardVariant(className?.cardCarousel?.textVariant),
      videoVariant: (() => {
        const base = createItgCardVariant(className?.cardCarousel?.videoVariant, { withMedia: true });
        return {
          ...base,
          cardContent: {
            ...base.cardContent,
            // wrapper: cn('justify-end mx-5 sm:mx-7 z-10', className?.cardCarousel?.videoVariant?.cardContent?.wrapper),
            header: {
              ...base.cardContent?.header,
              headline: cn(
                'font-sans text-[18px] leading-[120%] tracking-[-0.09px] sm:text-[24px] sm:tracking-[-0.12px]',
                className?.cardCarousel?.videoVariant?.cardContent?.header?.headline,
              ),
            },
          },
          transitions: {
            playing: cn(
              'transition-opacity duration-300 opacity-0 pointer-events-none',
              className?.cardCarousel?.videoVariant?.transitions?.playing,
            ),
            paused: cn(
              'transition-opacity duration-300 opacity-100',
              className?.cardCarousel?.videoVariant?.transitions?.paused,
            ),
          },
        };
      })(),
    },
  };
  const classNamesByVariant: Record<ContentFeatureCarouselVariant, ContentFeatureCarouselClassNames> = {
    [CONTENT_FEATURE_CAROUSEL_VARIANTS.Base]: baseClassNames,
    [CONTENT_FEATURE_CAROUSEL_VARIANTS.ITG]: itgClassNames,
  };

  const internalClassNames =
    classNamesByVariant[variant] ?? classNamesByVariant[CONTENT_FEATURE_CAROUSEL_VARIANTS.Base];

  const testFixture = 'content-feature-carousel';

  return (
    <section
      data-testid="section"
      {...(sectionName != null && { id: encodeURIComponent(sectionName) })}
      {...liveUpdateAttrs(sys?.id, 'contentFeatureCarousel')}
      className={cn(internalClassNames.section, getYMargins(margins), getColorway(color))}>
      <div className={internalClassNames.wrapper}>
        <div className={internalClassNames.header.wrapper} data-testid={`${testFixture}-header-wrapper`}>
          <Eyebrow sys={sys} eyebrowText={eyebrow} className={internalClassNames.header.eyebrow} />
          <div className={internalClassNames.header.headline} {...liveUpdateAttrs(sys?.id, 'headline')}>
            <RichTextHeadline headline={headline} level="h2" />
          </div>

          <div
            {...liveUpdateAttrs(sys?.id, 'copy')}
            className={internalClassNames.copy}
            data-testid={`${testFixture}-copy`}>
            <RichTextCopy copy={copy} />

            <div ref={paginationRef} className="hidden sm:flex" data-root-classname="flex sm:hidden" />
          </div>
          {isValidCta(cta) && (
            <CTA
              cta={cta}
              testId={`${testFixture}-cta`}
              className={cn('hidden sm:inline-flex', internalClassNames.cta)}
            />
          )}
        </div>
        <CardCarousel
          {...cardCarousel}
          paginationPortalRef={paginationRef}
          className={{ ...internalClassNames.cardCarousel }}
          margins={Margins.NoMargins}
          color={color}
          getCarouselPaginationThreshold={paginationThreshold}
        />

        {isValidCta(cta) && (
          <CTA cta={cta} testId={`${testFixture}-cta`} className={cn('sm:hidden', internalClassNames.cta)} />
        )}
      </div>
    </section>
  );
};

export default ContentFeatureCarousel;
